import { useMutation, useQuery } from '@apollo/client';
import React, { createContext, useContext, useEffect, useState } from 'react';
import Joyride from 'react-joyride';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { GetPageHomeDocument } from '@/__generated__/graphql.js';
import { MyHiveLocalizationKey } from '@/localization/my-hive.js';
import ShareTenantsModal from '@/pages/asset/components/ShareTenantsModal.js';
import type { AssetRouteParams } from '@/pages/types.js';
import { GET_MYHIVE_ONBOARDING, SET_MYHIVE_ONBOARDING } from '@/services/user.service.js';

import type { Flow, OnboardingStep } from './lib/steps.js';
import { allSteps } from './lib/steps.js';
import OnboardingTooltipCard from './OnboardingTooltip.js';

export interface MyHiveOnboardingProgress {
  activeFlow?: Flow;
  completedFlows?: string[];
  seen?: string[];
  updatedAt?: string;
}

interface AssetOnboardingContextValue {
  start: () => void;
  stop: () => void;
  isActive: boolean;
  paused: boolean;
  setPaused: (value: boolean) => void;
  activeStep?: string;
  doReRun: () => void;
}

const AssetOnboardingContext = createContext<AssetOnboardingContextValue | undefined>(undefined);

function isMobile(): boolean {
  return window.matchMedia('(max-width: 1024px)').matches;
}

export const TourProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [run, setRun] = useState(false);
  const [reRun, setReRun] = useState(false);
  const [paused, setIsPaused] = useState(false);
  const [shareWithTenantsOpen, setShareWithTenantsOpen] = useState<boolean>(false);

  const { assetSlug } = useParams<keyof AssetRouteParams>();
  const { data } = useQuery(GetPageHomeDocument, {
    variables: { slug: assetSlug ?? '' },
    skip: !assetSlug || assetSlug === 'auth0-success',
  });
  const [searchParams, setSearchParams] = useSearchParams();
  const [step, setStep] = useState<OnboardingStep | null>(null);

  const { data: onboardingData, loading: loadingProgress } = useQuery(GET_MYHIVE_ONBOARDING, {
    fetchPolicy: 'cache-first',
    variables: { pageId: data?.page.id ?? '' },
    skip: !data?.page.id,
  });
  const [setProgressMutation] = useMutation(SET_MYHIVE_ONBOARDING);

  const progress = onboardingData?.getMyHiveOnboardingProgress;
  const progressLoaded = !loadingProgress && !!progress;
  const seen = progress?.seen ?? [];
  const steps = progressLoaded ? allSteps.filter((s) => !s.cohort || s.cohort === progress.cohort) : [];
  const unseenSteps = steps.filter((s) => !seen.includes(s.id));
  const skipTour = searchParams.get('skiptour') === 'true';

  useEffect(() => {
    if (!progressLoaded) return;
    const startAtId = searchParams.get('obsaid');
    if (reRun || skipTour) {
      return;
    } else if (startAtId) {
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev.toString());
        newParams.delete('obsaid');
        return newParams;
      });
      const startAtStep = allSteps.find((s) => s.id === startAtId);
      if (startAtStep) {
        setStep(startAtStep);
        setRun(true);
        return;
      } else {
        console.warn('Invalid startAtId provided in URL:', startAtId);
      }
    } else if (seen.length > 0 && unseenSteps.length > 0 && !step) {
      const lastStep = steps[steps.length - 1];
      const sawLastStep = lastStep ? seen.includes(lastStep.id) : false;

      if (!sawLastStep) {
        setStep(unseenSteps[0] ?? null);
        setRun(true);
        return;
      }
    }
  }, [searchParams, setSearchParams, step, unseenSteps, steps, progressLoaded, seen, reRun, skipTour]);

  useEffect(() => {
    const currentCompletedFlows = progress?.completedFlows ?? [];
    if (!step || seen.includes(step.id) || !progressLoaded || !run || reRun || skipTour) return;
    void (async () =>
      await setProgressMutation({
        variables: {
          progress: {
            activeFlow: step.flow,
            completedFlows: Array.from(new Set([...currentCompletedFlows, step.flow])),
            seen: Array.from(new Set([...seen, step.id])),
            updatedAt: new Date().toISOString(),
            cohort: progress.cohort ?? null,
            startedAt: progress.startedAt ?? progress.updatedAt ?? new Date().toISOString(),
          },
        },
        refetchQueries: [GET_MYHIVE_ONBOARDING],
      }))();
  }, [setProgressMutation, step, progressLoaded, progress, seen, run, reRun, skipTour]);

  const stop = (): void => {
    setRun(false);
  };

  const getNextStep = (currentStep: OnboardingStep | null): OnboardingStep | null => {
    if (!currentStep) return null;
    const currentIndex = steps.findIndex((s) => s.id === currentStep.id);
    if (currentIndex === -1) return null;
    for (let i = currentIndex + 1; i < steps.length; i++) {
      const candidate = steps[i];
      if (candidate && (!candidate.cohort || candidate.cohort === progress?.cohort)) {
        return reRun ? candidate : !seen.includes(candidate.id) ? candidate : null;
      }
    }
    return null;
  };

  const handleContinue = (): void => {
    setStep((prev) => {
      const nextStep = getNextStep(prev);
      if (!nextStep || !prev || prev.lastStep === true) {
        setRun(false);
        return null;
      } else {
        return nextStep;
      }
    });
  };

  const setPaused = (value: boolean): void => {
    if (value === true) {
      setIsPaused((prev) => {
        // don't double continue.
        !prev && handleContinue();
        return true;
      });
    } else {
      setIsPaused(false);
    }
  };

  const start = (): void => {
    setStep(steps[0] ?? null);
    setRun(true);
  };

  const navigate = useNavigate();

  let orangeButton:
    | {
        label: string;
        onClick: () => void;
      }
    | undefined;

  if (step?.id === 'invite') {
    orangeButton = {
      label: MyHiveLocalizationKey.InviteTenantsNow,
      onClick: () => {
        setPaused(true);
        setShareWithTenantsOpen(true);
      },
    };
  }

  if (step?.id === 'register' || step?.id === 'book') {
    orangeButton = {
      label: MyHiveLocalizationKey.BookEventNow,
      onClick: () => {
        setPaused(true);
        navigate(`${location.pathname}/workshops`);
      },
    };
  }
  const shouldRun = run && !paused && !isMobile() && !skipTour;
  const doReRun = (): void => {
    skipTour &&
      setSearchParams((prev) => {
        const newParams = new URLSearchParams(prev.toString());
        newParams.delete('skiptour');
        return newParams;
      });
    setStep(steps[0] ?? null);
    setReRun(true);
    setRun(true);
  };

  return (
    <AssetOnboardingContext.Provider
      value={{
        start,
        stop,
        isActive: run,
        paused,
        setPaused,
        activeStep: shouldRun ? step?.id : undefined,
        doReRun,
      }}
    >
      <Joyride
        steps={shouldRun ? allSteps : []}
        run={shouldRun}
        stepIndex={step ? allSteps.findIndex((s) => s.id === step.id) : undefined}
        continuous
        showSkipButton={false}
        tooltipComponent={(p) =>
          shouldRun ? (
            <OnboardingTooltipCard
              flow={step?.flow ?? undefined}
              {...p}
              orangeButton={orangeButton}
              handleContinue={handleContinue}
              isLastStep={!getNextStep(step)}
            />
          ) : null
        }
        styles={{
          options: { zIndex: 9999, overlayColor: 'rgba(0,0,0,0.7)' },
          tooltip: { padding: 0, backgroundColor: 'transparent', maxWidth: 'none' },
          tooltipContainer: { textAlign: 'left' },
        }}
        disableScrolling
        disableOverlayClose={true}
        disableCloseOnEsc={true}
        scrollToFirstStep={false}
        floaterProps={{ hideArrow: true }}
      />
      <ShareTenantsModal
        pageId={data?.page.id ?? ''}
        pageSlug={data?.page.slug ?? ''}
        pageTitle={data?.page.title ?? ''}
        open={shareWithTenantsOpen}
        onClose={() => {
          setPaused(false);
          setShareWithTenantsOpen(false);
        }}
      />
      {children}
    </AssetOnboardingContext.Provider>
  );
};

export const useAssetOnboarding = (): AssetOnboardingContextValue => {
  const context = useContext(AssetOnboardingContext);
  return (
    context ?? {
      start: () => {
        // throw new Error('useAssetOnboarding must be used within a TourProvider');
      },
      stop: () => {
        // throw new Error('useAssetOnboarding must be used within a TourProvider');
      },
      paused: false,
      setPaused: () => {
        // throw new Error('useAssetOnboarding must be used within a TourProvider');
      },
      isActive: false,
      doReRun: () => {
        // throw new Error('useAssetOnboarding must be used within a TourProvider');
      },
    }
  );
};
